<template>
  <div class="visible-by-selector">
    <div class="selector-header">
      <div class="search-container">
        <CInput
          v-model="searchQuery"
          :placeholder="$pgettext('roles.visible_by', 'Search roles...')"
          class="search-input">
          <template #prepend>
            <CIcon name="cilSearch" class="search-icon"/>
          </template>
          <template #append v-if="searchQuery">
            <CButton
              @click="searchQuery = ''"
              variant="ghost"
              size="sm"
              class="clear-btn">
              <CIcon name="cilX" size="sm"/>
            </CButton>
          </template>
        </CInput>
      </div>
      <div class="selected-count">
        <translate translate-context="roles.visible_by" :translate-params="{count: selectedRoles.length}">
          %{count} selected
        </translate>
      </div>
    </div>

    <div class="roles-container">
      <div v-if="selectedFilteredRoles.length > 0" class="selected-section">
        <div class="section-header">
          <translate translate-context="roles.visible_by">Selected Roles</translate>
        </div>
        <div class="roles-grid">
          <div
            v-for="role in selectedFilteredRoles"
            :key="role.roleId"
            class="role-item selected"
            @click="toggleRole(role.roleId)">
            <CIcon name="cilCheckCircle" class="check-icon selected"/>
            <span class="role-name">{{ role.name }}</span>
          </div>
        </div>
      </div>

      <div v-if="availableFilteredRoles.length > 0" class="available-section">
        <div v-if="selectedFilteredRoles.length > 0" class="section-header">
          <translate translate-context="roles.visible_by">Available Roles</translate>
        </div>
        <div class="roles-grid">
          <div
            v-for="role in visibleAvailableRoles"
            :key="role.roleId"
            class="role-item"
            @click="toggleRole(role.roleId)">
            <CIcon name="cilCircle" class="check-icon"/>
            <span class="role-name">{{ role.name }}</span>
          </div>
        </div>
        <div v-if="hasMoreAvailable" class="show-more-container">
          <CButton
            @click="showMoreRoles"
            variant="ghost"
            color="primary"
            class="show-more-btn">
            <CIcon name="cilArrowBottom" class="mr-2"/>
            <translate translate-context="roles.visible_by" :translate-params="{count: remainingCount}">
              Show %{count} more
            </translate>
          </CButton>
        </div>
      </div>

      <div v-if="hiddenSelectedRoles.length > 0" class="hidden-section">
        <div class="section-header">
          <translate translate-context="roles.visible_by">Hidden Selected Roles</translate>
          <CIcon name="cilInfo" v-c-tooltip="hiddenTooltip" class="info-icon"/>
        </div>
        <div class="hidden-roles-info">
          <translate translate-context="roles.visible_by" :translate-params="{count: hiddenSelectedRoles.length}">
            %{count} role(s) selected but not visible in current context
          </translate>
        </div>
      </div>

      <div v-if="noResults" class="no-results">
        <CIcon name="cilSearch" class="no-results-icon"/>
        <translate translate-context="roles.visible_by">No roles found</translate>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VisibleBySelector',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    availableRoles: {
      type: Array,
      default: () => []
    },
    contextId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      searchQuery: '',
      itemsPerPage: 10,
      currentPage: 1,
      selectedRoles: [...this.value]
    }
  },
  computed: {
    hiddenTooltip() {
      return this.$pgettext('roles.visible_by.tooltip', 'These roles are selected but not visible in your current context. They will be preserved when saving.')
    },
    normalizedRoles() {
      return this.availableRoles.map(role => ({
        ...role,
        roleId: role.id  // Usa sempre l'ID numerico
      }))
    },
    filteredRoles() {
      if (!this.searchQuery) {
        return this.normalizedRoles
      }
      const query = this.searchQuery.toLowerCase()
      return this.normalizedRoles.filter(role =>
        role.name.toLowerCase().includes(query)
      )
    },
    selectedFilteredRoles() {
      return this.filteredRoles.filter(role =>
        this.selectedRoles.includes(role.roleId)
      )
    },
    availableFilteredRoles() {
      return this.filteredRoles.filter(role =>
        !this.selectedRoles.includes(role.roleId)
      )
    },
    visibleAvailableRoles() {
      const maxItems = this.currentPage * this.itemsPerPage
      return this.availableFilteredRoles.slice(0, maxItems)
    },
    hasMoreAvailable() {
      return this.availableFilteredRoles.length > this.visibleAvailableRoles.length
    },
    remainingCount() {
      return this.availableFilteredRoles.length - this.visibleAvailableRoles.length
    },
    hiddenSelectedRoles() {
      const availableIds = this.normalizedRoles.map(r => r.roleId)
      return this.selectedRoles.filter(id => !availableIds.includes(id))
    },
    noResults() {
      return this.searchQuery &&
        this.selectedFilteredRoles.length === 0 &&
        this.availableFilteredRoles.length === 0
    }
  },
  watch: {
    value(newVal) {
      this.selectedRoles = [...newVal]
    },
    searchQuery() {
      this.currentPage = 1
    }
  },
  methods: {
    toggleRole(key) {
      const index = this.selectedRoles.indexOf(key)
      if (index > -1) {
        this.selectedRoles.splice(index, 1)
      } else {
        this.selectedRoles.push(key)
      }
      this.$emit('input', this.selectedRoles)
    },
    showMoreRoles() {
      this.currentPage++
    }
  }
}
</script>

<style scoped>
.visible-by-selector {
  width: 100%;
}

.selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
}

.search-container {
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
}

.search-icon {
  color: #64748b;
  padding: 0 0.5rem;
}

.clear-btn {
  padding: 0.25rem;
  border: none;
  background: transparent;
}

.clear-btn:hover {
  background: #f1f5f9;
  border-radius: 0.25rem;
}

.selected-count {
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
}

.roles-container {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #ffffff;
  max-height: 500px;
  overflow-y: auto;
}

.section-header {
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 600;
  font-size: 0.875rem;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-icon {
  color: #94a3b8;
  cursor: help;
}

.roles-grid {
  padding: 0.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem;
}

.role-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.role-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.role-item.selected {
  background: #eff6ff;
  border-color: #3b82f6;
}

.role-item.selected:hover {
  background: #dbeafe;
  border-color: #2563eb;
}

.check-icon {
  color: #cbd5e1;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.check-icon.selected {
  color: #3b82f6;
}

.role-name {
  font-size: 0.875rem;
  color: #1e293b;
  font-weight: 500;
}

.selected-section {
  border-bottom: 1px solid #e2e8f0;
}

.available-section {
  position: relative;
}

.show-more-container {
  padding: 1rem;
  text-align: center;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.show-more-btn {
  font-weight: 500;
}

.hidden-section {
  background: #fef3c7;
  border-top: 1px solid #fde68a;
}

.hidden-section .section-header {
  background: #fef3c7;
  border-bottom: 1px solid #fde68a;
  color: #92400e;
}

.hidden-roles-info {
  padding: 1rem;
  color: #92400e;
  font-size: 0.875rem;
}

.no-results {
  padding: 3rem;
  text-align: center;
  color: #94a3b8;
}

.no-results-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

@media (max-width: 640px) {
  .selector-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-container {
    max-width: 100%;
  }

  .selected-count {
    text-align: center;
  }

  .roles-grid {
    grid-template-columns: 1fr;
  }
}
</style>